import { cssRaw, style } from 'typestyle';
import { type CSSProperties, type NestedCSSProperties } from 'typestyle/lib/types';
import DashboardTheme from '../statistics/dashboard/dashboard-theme';
import StyleConstants from '../../core/styles.constants';

// Table chrome for the office detail tabs, matching the statistics dashboard tables (Reporting →
// rooms/employees) so both admin sections look like one product.
//
// These rules are deliberately kept in step with `statistics/dashboard/tabs/reporting-tab.styles.ts`
// rather than shared from one place: extracting them would mean repointing every statistics tab,
// which is out of scope here. If you restyle a table on either side, change both — and if a third
// feature needs the same chrome, promote this file (plus SortableHeaderCell / TablePagination /
// table-controls.hook, which offices already imports from statistics) into `core/`.
export const tableWrap = style({
  overflowX: 'auto',
  minWidth: 0,
  background: DashboardTheme.white,
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: 12,
});

cssRaw(
  `
  .${tableWrap} tbody tr {
    transition: background-color .12s ease;
  }
  .${tableWrap} tbody tr:hover {
    background-color: ${DashboardTheme.gray50};
  }
  `,
);

export const table: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
};

export const tableHeaderCell: CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: DashboardTheme.gray700,
  background: DashboardTheme.gray50,
  borderBottom: `1px solid ${DashboardTheme.gray200}`,
  whiteSpace: 'nowrap',
  fontFamily: DashboardTheme.bodyFontFamily,
};

export const tableCell: CSSProperties = {
  padding: '10px 12px',
  fontSize: 13,
  fontWeight: 500,
  color: DashboardTheme.navy800,
  borderBottom: `1px solid ${DashboardTheme.gray100}`,
  whiteSpace: 'nowrap',
  fontFamily: DashboardTheme.bodyFontFamily,
};

export const actionCell: CSSProperties = {
  ...tableCell,
  width: 1,
  textAlign: 'right',
};

// Placeholder for a first/last name the backend has no value for yet (the permitted user was
// invited by e-mail and hasn't signed in), so the column never renders as a blank hole.
export const mutedCell: CSSProperties = {
  ...tableCell,
  color: DashboardTheme.gray500,
};

export const controlsRow = (isMobile: boolean): CSSProperties => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  alignItems: isMobile ? 'stretch' : 'center',
  gap: 10,
  flexWrap: 'wrap',
  marginBottom: 14,
});

export const searchInput = (isMobile: boolean): CSSProperties => ({
  width: isMobile ? '100%' : 240,
  padding: '9px 14px',
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: 10,
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 13.5,
  color: DashboardTheme.navy800,
  background: DashboardTheme.white,
});

export const addRow = (isMobile: boolean): CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginLeft: isMobile ? 0 : 'auto',
});

export const addInput = (isMobile: boolean): CSSProperties => ({
  ...searchInput(isMobile),
  width: isMobile ? '100%' : 260,
  flex: isMobile ? 1 : undefined,
});

// Same blue as PrimaryButton (StyleConstants.MEDIUM_BLUE_COLOR) so table actions don't introduce
// a second primary colour next to the app's existing buttons.
export const addButton: NestedCSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  border: `1px solid ${StyleConstants.MEDIUM_BLUE_COLOR}`,
  background: StyleConstants.MEDIUM_BLUE_COLOR,
  color: StyleConstants.WHITE_COLOR,
  borderRadius: 10,
  padding: '9px 14px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  fontFamily: DashboardTheme.bodyFontFamily,
  transitionDuration: '200ms',
  transitionProperty: 'background-color',
  $nest: {
    '&:hover': {
      background: StyleConstants.DARK_BLUE_COLOR,
      borderColor: StyleConstants.DARK_BLUE_COLOR,
    },
  },
};

export const rowActionButton: NestedCSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  padding: 0,
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: 8,
  background: DashboardTheme.white,
  color: DashboardTheme.gray500,
  cursor: 'pointer',
  $nest: {
    '&:hover': {
      color: StyleConstants.PINK_COLOR,
      borderColor: StyleConstants.PINK_COLOR,
    },
  },
};

export const rowsPerPageRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 12.5,
  color: DashboardTheme.gray700,
};

export const rowsPerPageSelect: CSSProperties = {
  padding: '7px 10px',
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: 10,
  background: DashboardTheme.white,
  color: DashboardTheme.navy800,
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 12.5,
  cursor: 'pointer',
};

export const emptyMessage: CSSProperties = {
  display: 'block',
  color: DashboardTheme.gray500,
  fontSize: 13.5,
  padding: '12px 0',
  fontFamily: DashboardTheme.bodyFontFamily,
};

export const errorMessage: CSSProperties = {
  ...emptyMessage,
  color: DashboardTheme.red500,
  fontWeight: 600,
};
